import {
  CustomSelect,
  toast,
  Tooltip,
  UnifiedDateTimePicker,
} from "@/components";
import { RootState } from "@/store/index";
import { connect } from "react-redux";
import { useEffect, useState, useRef, useMemo } from "react";
import { ConnectedProps } from "react-redux";
import DenmarkPublishPopover from "./DenmarkPublishPopover";
import DenmarkEditModal from "./DenmarkEditModal";
import DenmarkChipsDisplay from "./DenmarkChipsDisplay";
import FavSectionChips from "./FavSectionChips";
import Denmark from "@/assets/icons/svg/denmark_fav.svg";
import { Star } from "lucide-react";
import {
  callPublishMedia,
  fetchHostSection,
  updatePublishMedia,
  getUpdateMedia,
  markFavorite,
  removeFavorite,
} from "@/store/media/media.action";
import { getScheduleTime } from "@/utils/mediautils/CommonFunctions";
import {
  callDenmarkDetailApi,
  callDenmarkArticleGetApi,
} from "@/store/upload/upload.action";
import { useDispatch } from "react-redux";
import {
  DenmarkPublish as DenmarkPublishInterface,
  HostSubSection,
} from "@/database";
import { getMostFrequentNoIndex } from "@/store/user/user.action";
import SandboxIcon from "@/assets/icons/svg/hourglass.svg?react";

import {
  formatTimestampToAgo,
  getDenmarkMediaUrlWithSeopath,
  getHostLogoStyleByHostId,
  socialPublishStatusToTextAndColor,
  useClickOutside,
  createHostOptions,
} from "@/utils";
import { deleteDenmarkPublish } from "@/store/media/media.action";
import AlertDialog from "@/utils/ui/AlertDialog/AlertDialog";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import { DENMARK_STATUS } from "@/utils/constants/uiConstants";
import { fetchDenmarkEditSectionSelectStub } from "@/database/media/denmarkEditSectionSelectStubApi";

const DenmarkPublish = ({
  hosts,
  hostSubsection,
  mostFrequentDenmark,
  selectedHost,
  handleDenmarkchange,
  handlehostChange,
  currentSelectedSection,
  handleCancelOrDelete,
  onQueueDenmarkDelete,
  selectedSections,
  hasInFormDenmark = false,
  isPreview = false,
  labelCheckboxChecked = false,
  onLabelCheckboxChange = () => {},
  readOnlyMode = false,
  disabled = false,
  isPreset = false,
  useArticleSearch = false,
  onArticleSearch,
  hostsArray,
  currentMediaDetailsForEdit,
  denmarkHosts,
  allHostsList,
  isDraft,
  schedule_after,
  onScheduleAfterChange,
  editedSections,
  onSaveAndPublish,
  onClearEditedSections,
  showActions = false,
  globalFavSectionList,
  markFavorite,
  removeFavorite,
}: Props & {
  handleDenmarkchange: (section: any, actionMeta: any) => void;
  handlehostChange: (host: any, source?: "effect" | "select") => void;
  currentSelectedSection: DenmarkPublishInterface[];
  editedSections?: DenmarkPublishInterface[];
  selectedHost: string;
  handleCancelOrDelete: (id: number, type: string) => void;
  /**
   * When provided, deleting a SAVED Denmark chip is deferred: the chip is removed
   * from the UI now and the delete API call is queued to fire on form save,
   * instead of deleting immediately. (Embedded / Media Detail form flow.)
   */
  onQueueDenmarkDelete?: (
    item: { id?: string | number; section?: number } | string | number
  ) => void;
  selectedSections: DenmarkPublishInterface[];
  hasInFormDenmark?: boolean;
  isPreview?: boolean;
  labelCheckbox?: boolean;
  labelCheckboxChecked?: boolean;
  onLabelCheckboxChange?: any;
  readOnlyMode?: boolean;
  disabled?: boolean;
  isMarginRequired?: boolean;
  isPreset?: boolean;
  useArticleSearch?: boolean;
  onArticleSearch?: (query: string, host?: string) => void;
  hostsArray?: any[];
  isDraft?: boolean;
  schedule_after?: number;
  onScheduleAfterChange?: (value: number) => void;
  onSaveAndPublish?: (updatedSections: DenmarkPublishInterface[]) => void;
  onClearEditedSections?: () => void;
  showActions?: boolean;
}) => {
  const dispatch = useDispatch();
  const [MFDenmark, setMFDenmark] = useState<any>([]);
  const [popoverChip, setPopoverChip] = useState<{
    chip: DenmarkPublishInterface;
    anchor: DOMRect;
  } | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const prevSelectedHostRef = useRef<string | undefined>(selectedHost);
  const hostChangeTsRef = useRef<number>(0);
  const lastScheduleAfterRef = useRef<number | null>(null);
  const hasUserInteractedWithScheduleRef = useRef<boolean>(false);
  const initialDateTimePickerTimestampRef = useRef<number | null>(null);
  const [customScheduleTimestamp, setCustomScheduleTimestamp] = useState<
    number | undefined
  >(undefined);

  useClickOutside(searchContainerRef, () => {
    if (useArticleSearch && onArticleSearch) {
      onArticleSearch("");
    }
  });

  const hostOptions = createHostOptions(hosts, denmarkHosts);

  const handleDenmarkEdit = (publishData: DenmarkPublishInterface) => {
    setOpenPriorityPanelOnEdit(false);
    setDenmarkEditData(publishData);
    setDenmarkEditModalOpen(true);
    setPopoverChip(null);
  };

  /** Published Denmark row for a section (saved host social), for opening edit with server-backed data. */
  const getPublishedDenmarkForSection = (
    sectionId: number | null | undefined
  ): DenmarkPublishInterface | undefined => {
    if (sectionId == null || Number.isNaN(Number(sectionId))) return undefined;
    const sid = Number(sectionId);

    const fromSelected = (selectedSections || []).find(
      (p) =>
        (p.job_type === "denmark" || isPreset) &&
        Number(p.section) === sid &&
        typeof p.status === "number" &&
        p.status !== 100
    );
    if (fromSelected) return fromSelected;

    const fromSocials = (currentMediaDetailsForEdit?.socials || []).find(
      (s) =>
        s.job_type === "denmark" &&
        Number(s.section) === sid &&
        typeof s.status === "number" &&
        s.status !== 100
    );
    return fromSocials as DenmarkPublishInterface | undefined;
  };

  /**
   * Draft child rows use `_parentSectionId`; when the parent is already published, open using that published row.
   * For a draft top-level chip whose section is also published, prefer the published object.
   */
  const denmarkEditDataForCurrentSectionChip = (
    chip: DenmarkPublishInterface
  ): DenmarkPublishInterface => {
    const parentId = (chip as any)._parentSectionId;

    if (parentId != null) {
      const draftParent = (currentSelectedSection || []).find(
        (p) =>
          Number(p.section) === Number(parentId) &&
          !(p as any)._parentSectionId &&
          (p.job_type === "denmark" || isPreset)
      );
      if (draftParent) return draftParent;

      const publishedParent = getPublishedDenmarkForSection(Number(parentId));
      if (publishedParent) return publishedParent;

      return chip;
    }

    const publishedSelf = getPublishedDenmarkForSection(chip.section);
    if (publishedSelf) return publishedSelf;

    return chip;
  };

  const handleAddToPriority = (publishData: DenmarkPublishInterface) => {
    setOpenPriorityPanelOnEdit(true);
    setDenmarkEditData(publishData);
    setDenmarkEditModalOpen(true);
    setPopoverChip(null);
  };

  const showPopover = !readOnlyMode && !isPreview && !isPreset;
  const [showDenmarkRemovalAlert, setShowDenmarkRemovalAlert] = useState(false);
  const [denmarkToDelete, setDenmarkToDelete] = useState<{
    id: string;
    section?: number;
  } | null>(null);
  const [willDeleteEmbeds, setWillDeleteEmbeds] = useState(false);
  const [multiTargetToDelete, setMultiTargetToDelete] = useState<{
    target: DenmarkPublishInterface;
    parent: DenmarkPublishInterface;
  } | null>(null);
  const [denmarkEditModalOpen, setDenmarkEditModalOpen] = useState(false);
  const [denmarkEditData, setDenmarkEditData] =
    useState<DenmarkPublishInterface | null>(null);
  const [scrollToMultiSection, setScrollToMultiSection] = useState<
    number | undefined
  >(undefined);
  const [openPriorityPanelOnEdit, setOpenPriorityPanelOnEdit] = useState(false);

  const checkIfLastDenmarkWithEmbeds = () => {
    if (!isDraft) return false;

    // Get all Denmark videoshows from selectedSections only
    const denmarkVideoshows = (selectedSections || []).filter(
      (s) => s.job_type === "denmark"
    );

    // Get all Denmark embeds from selectedSections only
    const denmarkEmbeds = (currentMediaDetailsForEdit?.socials || []).filter(
      (s) => s.job_type === "denmark_embed"
    );

    const hasDenmarkEmbeds = denmarkEmbeds.length > 0;
    const isLastDenmarkVideoshow = denmarkVideoshows.length === 1;

    return isLastDenmarkVideoshow && hasDenmarkEmbeds;
  };

  const renderDraftDeleteButton = (item: DenmarkPublishInterface) => (
    <i
      className="bi bi-x fs-4"
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        // Only check if deleting Denmark videoshow, not Denmark embed
        if (item.job_type === "denmark") {
          setWillDeleteEmbeds(checkIfLastDenmarkWithEmbeds());
        } else {
          setWillDeleteEmbeds(false);
        }
        setDenmarkToDelete({ id: item.id, section: item.section });
        setShowDenmarkRemovalAlert(true);
      }}
    />
  );

  const deleteAllDenmarkEmbeds = async () => {
    // Only delete Denmark embeds from selectedSections
    const denmarkEmbeds = (currentMediaDetailsForEdit?.socials || []).filter(
      (s) => s.job_type === "denmark_embed"
    );

    // Delete all Denmark embeds
    for (const embed of denmarkEmbeds) {
      if (embed.id) {
        await dispatch(deleteDenmarkPublish({ id: embed.id }) as any);
      }
    }
  };

  // Wrap host change so the parent knows from where it was triggered
  const handleHostChangeFromEffect = (newSelectedHost: any) => {
    // Prevent infinite loop: only call if the value is actually different
    if (
      newSelectedHost?.value &&
      String(newSelectedHost.value) !== String(selectedHost)
    ) {
      handlehostChange(newSelectedHost, "effect");
    }
  };

  const handleHostChangeFromSelect = (newSelectedHost: any) => {
    handlehostChange(newSelectedHost, "select");
  };

  const customFetchHostSection = (params: any) => {
    if (useArticleSearch && onArticleSearch) {
      const searchQuery = params.keyword || params.inputValue || "";

      // If host changed, clear the search and update the ref
      if (prevSelectedHostRef.current !== selectedHost) {
        prevSelectedHostRef.current = selectedHost;
        // Clear search when host changes, but still allow search if user is typing
        if (!searchQuery) {
          onArticleSearch("");
          return;
        }
        // If there's a search query, proceed with the search for the new host
      }

      // Trigger search with the query (will use current selectedHost)
      onArticleSearch(searchQuery, selectedHost);
    } else if (!useArticleSearch) {
      const hostToUse =
        selectedHost ||
        (hostsArray && hostsArray.length > 0 ? hostsArray[0].id : "");
      const paramsWithHost = {
        ...params,
        hostid: hostToUse,
      };
      dispatch(fetchHostSection(paramsWithHost) as any);
    }
  };

  useEffect(() => {
    // Skip if this is the same value we just processed
    if (lastScheduleAfterRef.current === schedule_after) {
      return;
    }

    // Reset the initial timestamp ref when scheduleAfter changes from props
    // This ensures we track the new initial value from DateTimePicker
    initialDateTimePickerTimestampRef.current = null;

    if (
      schedule_after !== undefined &&
      schedule_after !== null &&
      schedule_after > 0
    ) {
      // Check if scheduleAfter is epoch time (large number) or minutes (small number)
      const isEpochTime = schedule_after > 1000000000000; // Epoch time in milliseconds is > 1 trillion

      if (isEpochTime) {
        // It's already an epoch timestamp - this is a saved schedule
        setCustomScheduleTimestamp(schedule_after);
        lastScheduleAfterRef.current = schedule_after;
        hasUserInteractedWithScheduleRef.current = true; // Mark as already set (saved schedule)
      } else {
        // It's a value in minutes - convert to epoch time
        const timestamp = Date.now() + schedule_after * 60 * 1000;
        setCustomScheduleTimestamp(timestamp);
        lastScheduleAfterRef.current = timestamp;
        // Don't call onScheduleAfterChange here - only update local state
        // The parent formik value should only be updated when user explicitly selects a date/time
      }
    } else if (schedule_after === 0) {
      // When scheduleAfter is 0, show current time and date
      const currentTime = Date.now();
      setCustomScheduleTimestamp(currentTime);
      lastScheduleAfterRef.current = 0; // Keep as 0 to track that it was originally 0
      hasUserInteractedWithScheduleRef.current = false; // Reset - no saved schedule
    } else if (schedule_after === null || schedule_after === undefined) {
      setCustomScheduleTimestamp(undefined);
      lastScheduleAfterRef.current = schedule_after ?? null;
      hasUserInteractedWithScheduleRef.current = false; // Reset - no saved schedule
    }
  }, [schedule_after]);

  useEffect(() => {
    if (prevSelectedHostRef.current !== selectedHost) {
      prevSelectedHostRef.current = selectedHost;
      hostChangeTsRef.current = Date.now();
      // Clear search when host changes to ensure fresh search with new host
      if (useArticleSearch && onArticleSearch) {
        onArticleSearch("");
      }
    }
  }, [selectedHost, useArticleSearch, onArticleSearch]);
  useEffect(() => {
    if (mostFrequentDenmark?.list && selectedHost) {
      const filtered = mostFrequentDenmark.list.filter(
        (item: any) => String(item.channelId) === String(selectedHost)
      );

      const sorted = filtered.sort((a, b) => (b?.count || 0) - (a?.count || 0));
      setMFDenmark(sorted);
    }
  }, [mostFrequentDenmark, selectedHost]);

  const favMsIdSet = useMemo(
    () => new Set((globalFavSectionList ?? [])?.map((s) => s.msId)),
    [globalFavSectionList]
  );

  const buildSectionOption = (section: HostSubSection) => ({
    value: section.id,
    label: `${section.id} | ${section.name} | ${section.activationStatus}`,
    title: section.name,
    path: `${section.id} | ${section.path}`,
    status: section.activationStatus,
    parentid: section.parentid,
    isFav: favMsIdSet.has(section.id),
  });

  const MFUSectionOptions = useMemo(
    () => MFDenmark.map(buildSectionOption),
    [MFDenmark, favMsIdSet]
  );

  const sectionOptions = useMemo(
    () => hostSubsection.map(buildSectionOption),
    [hostSubsection, favMsIdSet]
  );

  const customOptionRenderer = (option: any) => (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
        <div>{option.title}</div>
        <div className="text-muted small">{option.path}</div>
        <div className="fs-6">{option.status}</div>
      </div>
      <span
        onClick={async (e) => {
          e.stopPropagation();
          const params = {
            msId: option.value,
            name: option.title,
            path: option.path,
            parents: [
              { parentId: option.parentid, hostId: Number(selectedHost) },
            ],
            activationStatus: "ACTIVE",
            entityType: "SECTION",
            type: "1-0",
          };
          if (option.isFav) {
            const res = await removeFavorite(params);
            if (res?.status === 200) {
              toast.success("Removed from favorites");
            } else {
              toast.error(
                res?.error || "Could not remove favorite, please try again"
              );
            }
          } else {
            const res = await markFavorite(params);
            if (res?.status === 200) {
              toast.success("Marked as favorite");
            } else {
              toast.error(
                res?.error || "Could not mark as favorite, please try again"
              );
            }
          }
        }}
        style={{ cursor: "pointer", flexShrink: 0 }}
      >
        <Star
          size={14}
          fill={option.isFav ? "#f5a623" : "none"}
          stroke={option.isFav ? "#f5a623" : "#6c757d"}
        />
      </span>
    </div>
  );

  const hostOptionRenderer = (option: any) => (
    <div className="d-flex align-items-center" style={{ maxWidth: "180px" }}>
      <div style={option.logoStyle} />
      <span className="ms-2 text-truncate" style={{ maxWidth: "140px" }}>
        {option.label}
      </span>
    </div>
  );

  const hostValueRenderer = (option: any) => {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <div style={option.logoStyle} />
      </div>
    );
  };

  const handleRouting = async () => {
    if (popoverChip?.chip?.status !== 10) return;
    if (popoverChip?.chip?.host && popoverChip?.chip?.id) {
      const url = await getDenmarkMediaUrlWithSeopath(
        popoverChip?.chip?.published_id || "",
        popoverChip?.chip?.host,
        dispatch,
        callDenmarkDetailApi,
        popoverChip?.chip?.job_type === "denmark_embed",
        callDenmarkArticleGetApi,
        allHostsList
      );
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.fail("Live URL could not be fetched");
      }
    }
  };

  const didInitHostRef = useRef(false);
  const lastInitializedHostRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hosts || hostOptions.length === 0) return;
    const initialOption =
      hostOptions.find((o: any) => String(o.value) === String(selectedHost)) ||
      hostOptions[0];

    // Prevent infinite loop: only call if host actually needs to be initialized
    const needsInitialization =
      !selectedHost ||
      !hostOptions.find((o: any) => String(o.value) === String(selectedHost));

    const shouldInitialize = needsInitialization || !didInitHostRef.current;

    // Prevent calling if we're already initializing the same host
    if (
      shouldInitialize &&
      lastInitializedHostRef.current !== initialOption?.value
    ) {
      lastInitializedHostRef.current = initialOption?.value || null;
      if (needsInitialization) {
        handleHostChangeFromEffect(initialOption);
      } else if (!didInitHostRef.current) {
        didInitHostRef.current = true;
        handleHostChangeFromEffect(initialOption);
      }
    }
  }, [hosts, hostOptions, selectedHost]);

  const handleDenmarkMediaUpdate = async (denmarkArray: any[]) => {
    if (!currentMediaDetailsForEdit) return;

    // If the active chip has a vod id (e.g. "vod_dup_xxx"), it is already a
    // persisted social-publish record — update it directly via socialpublish.update
    // instead of going through the full media-update flow.
    const vodItem = denmarkArray.find(
      (item: any) => typeof item.id === "string" && item.id.startsWith("vod")
    );

    if (vodItem) {
      try {
        const vodParams = {
          ...vodItem,
          tags:
            Array.isArray(vodItem.seotags) && vodItem.seotags.length > 0
              ? vodItem.seotags
              : (vodItem.tags ?? []),
          denmark_req: {
            title_for_url: vodItem.title_for_url,
          },
        };
        await dispatch(updatePublishMedia(vodParams, () => {}) as any);
        toast.success("Denmark publish updated successfully");
        onSaveAndPublish?.([]);
      } catch {
        toast.fail("Failed to update Denmark publish");
        throw new Error("Failed to update Denmark publish");
      }

      return;
    }

    const denmark = denmarkArray.map((item: any) => ({
      section: item.section,
      channel_name: item.channel_name,
      host: item.host,
      folder_path: item.folder_path,
      article_type: "api",
      article_category: "denmark",
      schedule: getScheduleTime({
        schedule_type: item?.schedule_type,
        schedule_after: item?.schedule_after,
        schedule: item?.schedule,
      }),
      schedule_type: item?.schedule_type,
      schedule_after: item?.schedule_after,
      taxonomy: item?.taxonomy,
      denmark_tags: Array.isArray(item?.seotags)
        ? item.seotags
        : (item?.tags ?? []),
      activationStatus: item?.activationStatus,
      denmark_title: item?.title,
      denmark_description: item?.desc ?? item?.description,
      denmark_privacy:
        item?.privacy ?? item?.activationStatus ?? DENMARK_STATUS.ACTIVE,
      title_for_url: item?.title_for_url,
      // Only include multi_publish_targets for parent chips; omit for standalone
      ...(Array.isArray(item.multi_publish_targets) &&
        item.multi_publish_targets.length > 0 && {
          multi_publish_targets: item.multi_publish_targets,
        }),
    }));

    try {
      await dispatch(
        getUpdateMedia({
          ...currentMediaDetailsForEdit,
          is_denmark: true,
          denmark,
        } as any) as any
      );
      toast.success("Denmark publish updated successfully");
      onSaveAndPublish?.([]);
    } catch {
      toast.fail("Failed to update Denmark publish");
      throw new Error("Failed to update Denmark publish");
    }
  };

  return (
    <div className={`d-flex flex-column ${isPreset ? "m-0" : "m-0"}`}>
      {denmarkEditModalOpen && denmarkEditData && (
        <DenmarkEditModal
          isOpen={denmarkEditModalOpen}
          onClose={() => {
            setDenmarkEditModalOpen(false);
            setDenmarkEditData(null);
            setScrollToMultiSection(undefined);
            onClearEditedSections?.();
          }}
          publishData={denmarkEditData}
          handleDenmarkchange={handleDenmarkchange}
          handleHostChangeFromSelect={handleHostChangeFromSelect}
          selectedHost={
            denmarkEditData != null && denmarkEditData.host != null
              ? String(denmarkEditData.host)
              : selectedHost
          }
          disabled={disabled}
          currentSelectedSection={currentSelectedSection}
          editedSections={editedSections}
          selectedSections={selectedSections}
          selectedChip={Number(
            denmarkEditData?.section || denmarkEditData?.id || null
          )}
          handlecancelOrDelete={handleCancelOrDelete}
          onSaveAndPublish={handleDenmarkMediaUpdate}
          openPriorityPanel={openPriorityPanelOnEdit}
          scrollToMultiSection={scrollToMultiSection}
          sectionTreeSelectStub={
            openPriorityPanelOnEdit
              ? undefined
              : fetchDenmarkEditSectionSelectStub
          }
        />
      )}
      <div
        className="d-flex align-items-center justify-content-between gap-2 "
        style={{ marginBottom: "4px" }}
      >
        <div className="d-flex align-items-center gap-2">
          {(isPreview || isPreset) && !readOnlyMode && (
            <input
              type="checkbox"
              checked={labelCheckboxChecked}
              onChange={() => onLabelCheckboxChange(!labelCheckboxChecked)}
            />
          )}
          <label>{useArticleSearch ? "Article" : "Videoshow"}</label>
        </div>
        {!useArticleSearch && !isPreview && !isPreset && !readOnlyMode && (
          <button
            type="button"
            style={{
              border: "none",
              background: "transparent",
              color: "#6c757d",
              cursor: "pointer",
              transition: "color 0.15s ease",
              // marginRight: "10px",
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color = "#0d6efd")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color = "#6c757d")
            }
            title="Publish type"
            onClick={() => {
              const allDenmarkChips = [
                ...(currentSelectedSection || []),
                ...(selectedSections || []),
              ].filter((item) => item.job_type === "denmark" || isPreset);

              if (allDenmarkChips.length === 0) {
                handleDenmarkEdit({} as DenmarkPublishInterface);
                return;
              }

              const publishedChip = allDenmarkChips.find(
                (item) => typeof item.status === "number" && item.status !== 0
              );

              handleDenmarkEdit(
                denmarkEditDataForCurrentSectionChip(
                  publishedChip || allDenmarkChips[0]
                )
              );
            }}
          >
            {/* <Send size={14} /> */}
            <i className="bi bi-sliders fs-5"></i>
          </button>
        )}
      </div>
      {!readOnlyMode && !isPreview && (
        <div
          className="d-flex demarkCont position-relative"
          ref={searchContainerRef}
        >
          <div className="postion-relative w-100">
            <CustomSelect
              className="w-100"
              name="selectedChannels"
              label=""
              value={[]}
              handleChange={handleDenmarkchange}
              options={MFUSectionOptions}
              disabled={
                disabled ||
                !hostOptions?.length ||
                (useArticleSearch
                  ? !(
                      hasInFormDenmark ||
                      currentMediaDetailsForEdit?.socials?.some(
                        (item) => item.job_type === "denmark"
                      )
                    )
                  : false)
              }
              hostSubSection={true}
              onInputChangeCallback={customFetchHostSection}
              callbackParams={selectedHost}
              customOptionRenderer={customOptionRenderer}
              updatedOptions={useArticleSearch ? [] : sectionOptions}
              hideArrow={true}
              customStyling={{
                padding: "0 10px 0 0",
              }}
              placeholder={
                useArticleSearch
                  ? "Search for articles"
                  : disabled || !hostOptions?.length
                    ? "Add denmark"
                    : "Add Denmark destination"
              }
              labelCheckbox={false}
              labelCheckboxChecked={labelCheckboxChecked}
              onLabelCheckboxChange={onLabelCheckboxChange}
              readOnlyMode={readOnlyMode}
              loader={!useArticleSearch}
            />
          </div>
          {!hosts || hosts.length === 0 ? (
            <Tooltip content="Request Denmark access">
              <div
                className="position-absolute right-0 bg-light cursor-pointer top-30 d-flex align-items-center justify-content-center border "
                style={{
                  width: "40px",
                  height: "40px",
                }}
              >
                <img
                  src={Denmark}
                  alt="Denmark"
                  style={{
                    width: "18px",
                    height: "18px",
                    opacity: 0.5,
                    filter: "grayscale(100%)",
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <CustomSelect
              className="position-absolute right-0 cursor-pointer top-30"
              name="selectedHost"
              value={(() => {
                const matched = hostOptions?.find(
                  (option) => String(option.value) === String(selectedHost)
                );
                return matched ? matched.value : hostOptions?.[0]?.value || "";
              })()}
              handleChange={handleHostChangeFromSelect}
              options={hosts && hosts.length > 0 ? hostOptions : []}
              ishost={true}
              hideArrow={true}
              disabled={
                disabled ||
                !hostOptions?.length ||
                (useArticleSearch
                  ? !(
                      hasInFormDenmark ||
                      currentMediaDetailsForEdit?.socials?.some(
                        (item) => item.job_type === "denmark"
                      )
                    )
                  : (() => {
                      // Disable when we already have 2 denmark destinations
                      // (max 1 parent + 1 child)
                      const publishedCount = (
                        currentMediaDetailsForEdit?.socials || []
                      ).filter(
                        (s) => s.job_type === "denmark" && s.status !== 100
                      ).length;
                      const draftParentCount = (
                        currentSelectedSection || []
                      ).filter(
                        (item) =>
                          item.job_type === "denmark" && !item._parentSectionId
                      ).length;
                      return publishedCount + draftParentCount >= 1;
                    })())
              }
              customOptionRenderer={hostOptionRenderer}
              customValueRenderer={hostValueRenderer}
              menuWidth="200px"
              menuAlign="extend-right"
              isMenuWidthFixed={false}
            />
          )}
        </div>
      )}
      <style>
        {`
          .truncate-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 200px;
          }
        `}
      </style>

      {!useArticleSearch && !isPreview && !isPreset && !readOnlyMode && (
        <FavSectionChips
          globalFavSectionList={globalFavSectionList ?? []}
          hosts={hosts}
          hostOptions={hostOptions}
          denmarkHosts={denmarkHosts}
          selectedHost={selectedHost}
          onSelect={(section) => {
            const option = {
              value: section.msId,
              label: `${section.msId} | ${section.name} | ${section.type ?? ""}`,
              title: section.name ?? "",
              path: section.path ?? "",
              status: section.type ?? "",
            };
            handleDenmarkchange(option, {});
          }}
        />
      )}

      {!useArticleSearch && (
        <DenmarkChipsDisplay
          currentSelectedSection={(currentSelectedSection || []).filter(
            (item) => item.job_type === "denmark" || isPreset
          )}
          selectedSections={selectedSections || []}
          socials={currentMediaDetailsForEdit?.socials || []}
          hosts={hosts}
          hostOptions={hostOptions}
          denmarkHosts={denmarkHosts}
          isDraft={isDraft}
          isPreset={isPreset}
          readOnlyMode={readOnlyMode}
          isPreview={isPreview}
          showPopover={showPopover}
          onChipClick={(item) =>
            handleDenmarkEdit(denmarkEditDataForCurrentSectionChip(item))
          }
          onCancelChip={(sectionId) => {
            handleCancelOrDelete(sectionId, "cancel");
            if (popoverChip?.chip.section === sectionId) setPopoverChip(null);
          }}
          onDeleteDraftChip={(item) => {
            if (item.job_type === "denmark") {
              setWillDeleteEmbeds(checkIfLastDenmarkWithEmbeds());
            } else {
              setWillDeleteEmbeds(false);
            }
            setDenmarkToDelete({ id: item.id, section: item.section });
            setShowDenmarkRemovalAlert(true);
          }}
          onPopoverChipClick={(chip, rect) => {
            if (popoverChip?.chip.section === chip.section) {
              setPopoverChip(null);
            } else {
              setPopoverChip({ chip, anchor: rect });
            }
          }}
          onMultiChipClick={(parent) => {
            setScrollToMultiSection(undefined);
            handleDenmarkEdit(parent);
          }}
          onMultiPopoverChipClick={(target, parent) => {
            setScrollToMultiSection(Number(target.section));
            handleDenmarkEdit(parent);
          }}
          onDeleteMultiTarget={(target, parent) =>
            setMultiTargetToDelete({ target, parent })
          }
        />
      )}
      {useArticleSearch && (
        <div className="g-2 mt-2 d-flex gap-2" style={{ flexWrap: "wrap" }}>
          {currentSelectedSection &&
            currentSelectedSection
              .filter(
                (item) =>
                  item.job_type === "denmark_embed" && item.status === 100
              )
              .map((item) => (
                <div
                  key={`embed-temp-${item.section || item.id}`}
                  className="social-chip gap-1 p-1"
                  style={{
                    cursor: "default",
                    position: "relative",
                    width: "46%",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ width: "calc(100% - 25px)" }}
                  >
                    {(() => {
                      const logoStyle = getHostLogoStyleByHostId(
                        item.host,
                        hosts,
                        hostOptions,
                        denmarkHosts
                      );
                      return <div style={logoStyle} />;
                    })()}
                    <span className="text-truncate flex-grow-1">
                      {item.channel_name}
                    </span>
                  </div>
                  {!readOnlyMode && !isPreview && (
                    <i
                      className="bi bi-x fs-4"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const sectionId = item.section || item.id;
                        handleCancelOrDelete(Number(sectionId), "cancel");
                        if (popoverChip?.chip.section === item.section) {
                          setPopoverChip(null);
                        }
                      }}
                    />
                  )}
                </div>
              ))}
          {selectedSections &&
            selectedSections
              .filter((item) => item.job_type === "denmark_embed")
              .filter(
                (item) =>
                  !currentSelectedSection?.some(
                    (chip) =>
                      (chip.section === item.section || chip.id === item.id) &&
                      chip.job_type === "denmark_embed"
                  )
              )
              .sort((a, b) => (a.ct ?? 0) - (b.ct ?? 0))
              .map((item) => (
                <div
                  key={`embed-published-${item.section || item.id}`}
                  className="social-chip gap-1 p-1"
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    maxWidth: "46%",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    if (popoverChip?.chip.section === item.section) {
                      setPopoverChip(null);
                    } else {
                      setPopoverChip({ chip: item, anchor: rect });
                    }
                  }}
                >
                  {(() => {
                    const logoStyle = getHostLogoStyleByHostId(
                      item.host,
                      hosts,
                      hostOptions,
                      denmarkHosts
                    );
                    return <div style={logoStyle} />;
                  })()}
                  <span
                    className="text-truncate flex-grow-1"
                    style={{ paddingRight: "10px" }}
                  >
                    {item.channel_name}
                  </span>
                  {!isDraft && (
                    <Tooltip
                      content={
                        item?.schedule &&
                        item.schedule > Date.now() &&
                        item.status === 10
                          ? `Scheduled On: ${new Date(
                              item.schedule
                            ).toLocaleString()}`
                          : item?.status === 10
                            ? `Published On: ${formatTimestampToAgo(
                                item?.schedule || item?.mt
                              )}`
                            : item?.status === -2 || item?.status === -1
                              ? (item?.msg ??
                                "Your content has failed to publish.")
                              : item?.status === 0
                                ? "Your content is in queue, uploading will begin once content is activated."
                                : "Your content is getting uploaded, currently in progress."
                      }
                    >
                      {item?.schedule &&
                      item.schedule > Date.now() &&
                      item.status === 10 ? (
                        <i
                          className="bi bi-clock text-secondary me-1"
                          style={{ fontSize: "1rem" }}
                        ></i>
                      ) : (
                        renderStatusIndicator(item.status)
                      )}
                    </Tooltip>
                  )}
                  {isDraft && renderDraftDeleteButton(item)}
                </div>
              ))}
        </div>
      )}
      {/* {!readOnlyMode &&
        !isPreview &&
        onScheduleAfterChange &&
        !useArticleSearch &&
        (() => {
          const hasDenmarkInCurrent = currentSelectedSection?.some(
            (item) => item.job_type === "denmark"
          );
          const hasDenmarkInSelected = selectedSections?.some(
            (item) => item.job_type === "denmark"
          );
          const isScheduleAfterValid =
            schedule_after !== null &&
            schedule_after !== 0 &&
            schedule_after !== undefined;

          // If scheduleAfter is valid, require both scheduleAfter AND selectedSections to have denmark (AND condition)
          if (hasDenmarkInSelected && !hasDenmarkInCurrent) {
            return hasDenmarkInSelected && isScheduleAfterValid;
          }

          // Otherwise, use the original logic
          return hasDenmarkInCurrent || hasDenmarkInSelected;
        })() && (
          <div className="mt-3">
            <UnifiedDateTimePicker
              mode="datetime"
              disabled={
                currentSelectedSection?.length === 0 ||
                // Only disable if there's a saved schedule in the database (not just formik value)
                // Check if any selected sections have a saved schedule
                (selectedSections?.some(
                  (item) =>
                    item.job_type === "denmark" &&
                    item.schedule &&
                    item.schedule > 0 &&
                    item.id // Only if it's a saved item (has id)
                ) ??
                  false)
              }
              onChange={(timestamp: number) => {
                setCustomScheduleTimestamp(timestamp);

                // Track the initial timestamp from DateTimePicker mount
                if (initialDateTimePickerTimestampRef.current === null) {
                  initialDateTimePickerTimestampRef.current = timestamp;
                  // Don't update formik on initial mount - just set local state
                  return;
                }

                // Only update formik if timestamp has changed from initial (user interaction)
                // or if there's already a saved schedule (to allow updates)
                if (timestamp > Date.now()) {
                  if (
                    hasUserInteractedWithScheduleRef.current ||
                    timestamp !== initialDateTimePickerTimestampRef.current
                  ) {
                    onScheduleAfterChange(timestamp);
                    hasUserInteractedWithScheduleRef.current = true;
                  }
                }
              }}
              value={customScheduleTimestamp}
              outputFormat="timestamp"
              timeAdded={15}
            />
          </div>
        )} */}
      {popoverChip &&
        popoverChip.chip.status !== 100 &&
        showPopover &&
        !(
          popoverChip.chip.job_type === "denmark_embed" &&
          popoverChip.chip.status === 100
        ) && (
          <DenmarkPublishPopover
            chip={popoverChip.chip}
            anchor={popoverChip.anchor}
            hostLogoStyle={
              hostOptions.find((host) => host.value === selectedHost)
                ?.logoStyle || {}
            }
            onClose={() => setPopoverChip(null)}
            handleRouting={handleRouting}
            publishData={popoverChip.chip}
            currentMediaDetailsForEdit={currentMediaDetailsForEdit}
            hosts={hosts}
            hostOptions={hostOptions}
            denmarkHosts={denmarkHosts}
            showActions={true}
            onEdit={handleDenmarkEdit}
            onAddToPriority={handleAddToPriority}
          />
        )}
      {showDenmarkRemovalAlert && denmarkToDelete && (
        <AlertDialog
          isOpen={showDenmarkRemovalAlert}
          onClose={() => {
            setShowDenmarkRemovalAlert(false);
            setDenmarkToDelete(null);
            setWillDeleteEmbeds(false);
          }}
          onConfirm={async () => {
            if (onQueueDenmarkDelete) {
              // Deferred flow (Media Detail form): remove the chip from the UI now
              // and queue the delete API call to fire when the form is saved.
              onQueueDenmarkDelete({
                id: denmarkToDelete.id,
                section: denmarkToDelete.section,
              });
              if (willDeleteEmbeds) {
                (currentMediaDetailsForEdit?.socials || [])
                  .filter((s) => s.job_type === "denmark_embed" && s.id)
                  .forEach((embed) =>
                    onQueueDenmarkDelete({
                      id: embed.id,
                      section: (embed as any).section,
                    })
                  );
              }
            } else {
              // Immediate flow (standalone): delete now.
              await dispatch(
                deleteDenmarkPublish({ id: denmarkToDelete.id }) as any
              );
              // Check again if we need to delete embeds (count should be 1 before deletion)
              if (willDeleteEmbeds) {
                await deleteAllDenmarkEmbeds();
              }
            }

            setShowDenmarkRemovalAlert(false);
            setDenmarkToDelete(null);
            setWillDeleteEmbeds(false);
          }}
          title="ALERT!"
          message={
            willDeleteEmbeds
              ? "By deleting this Denmark videoshow, all your article embeds will also be deleted. Do you want to continue?"
              : "Are you sure you want to delete this Denmark publish?"
          }
          confirmText="Confirm"
          cancelText="Cancel"
          confirmButtonType="warning"
        />
      )}
      {multiTargetToDelete && (
        <AlertDialog
          isOpen={true}
          onClose={() => setMultiTargetToDelete(null)}
          onConfirm={async () => {
            const { target, parent } = multiTargetToDelete;
            const updatedTargets = (parent.multi_publish_targets || []).filter(
              (t: any) => Number(t.section) !== Number(target.section)
            );
            await dispatch(
              updatePublishMedia(
                { ...parent, multi_publish_targets: updatedTargets } as any,
                () => {}
              ) as any
            );
            setMultiTargetToDelete(null);
          }}
          title="ALERT!"
          message="Are you sure you want to remove this multi-publish target?"
          confirmText="Confirm"
          cancelText="Cancel"
          confirmButtonType="warning"
        />
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  hosts: state.media.hostList.list,
  hostSubsection: state.media.hostSubSection.list,
  isPublishingMedia: state.media.mediaPublish.isPublishingMedia,
  mostFrequentDenmark: state.users.mostFrequentDenmark,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  allHostsList: state.constants.allHostsList.list,
  denmarkHosts:
    state.constants.allHostsList.list &&
    Array.isArray((state.constants.allHostsList.list as any)?.hosts)
      ? ((state.constants.allHostsList.list as any)?.hosts as any[])
      : Array.isArray(state.constants.allHostsList.list)
        ? (state.constants.allHostsList.list as any[])
        : [],
  globalFavSectionList: state.media.GlobalFavSectionList.list,
});
const mapDispatch = {
  callPublishMedia,
  updatePublishMedia,
  getMostFrequentNoIndex,
  markFavorite,
  removeFavorite,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(DenmarkPublish);
